import axios from 'axios';
import API_CONFIG from '../../config/apiConfig';

const TODO_SERVICE_URL = API_CONFIG.BASE_URL;

// Task status types
export interface TaskStatus {
  id: string;
  name: string;
  label: string;
  color: string;
  position: number;
  is_default: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

// Task types based on the backend model
export interface Task {
  id: string;
  organization_id: string;
  user_id: string;
  project_id?: string;
  parent_task_id?: string;
  title: string;
  description?: string;
  expected_outcome?: string;
  status: 'pending' | 'in_progress' | 'blocked' | 'completed' | 'cancelled' | 'in_review'; // Added in_review status
  task_status_id?: string; // New field linking to task_statuses table
  priority: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  completed_at?: string;
  estimated_hours?: number;
  actual_hours?: number;
  progress?: number;
  metadata?: Record<string, any>;
  position?: number;
  daily_rank?: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;

  // Work output fields
  work_output?: string;
  work_output_generated_at?: string;
  work_output_type?: 'markdown' | 'html' | 'text';
  ai_completion_data?: { citations?: any[] };
  assigned_ai_teammate?: string;
  assigned_user_agent_id?: string;
  ai_assignment_status?: string;
  assignment_type?: 'group' | 'ai_agent' | 'user' | 'workflow' | 'none';

  // Workflow assignment fields
  workflow_id?: string; // UUID of workflow to execute for this task
  workflow_execution_id?: string; // UUID of current workflow execution

  // Joined fields
  user_name?: string;
  project_name?: string;
  assigned_teammate_name?: string;
  assigned_teammate_type?: string;
  assigned_group_name?: string;
  assignee_ids?: string[];
  labels?: string[];
  comments?: TaskComment[];
  subtasks?: Task[];
  attachments?: TaskAttachment[];
}

export interface TaskComment {
  id: string;
  text: string;
  userId: string;
  userName: string;
  createdAt: string;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  storage_path: string;
  uploaded_at: string;
}

export interface CreateTaskDTO {
  title: string;
  description?: string;
  project_id?: string;
  parent_task_id?: string;
  status?: Task['status']; // Legacy field
  task_status_id?: string; // New field for custom statuses
  priority?: Task['priority'];
  due_date?: string;
  estimated_hours?: number;
  assignee_ids?: string[];
  label_ids?: string[];
  assigned_ai_teammate?: string; // AI teammate assignment
  assigned_user_agent_id?: string; // User agent ID assignment

  // Workflow assignment fields
  workflow_id?: string; // UUID of workflow to execute for this task

  metadata?: Record<string, any>;
}

export interface UpdateTaskDTO {
  title?: string;
  description?: string;
  expected_outcome?: string;
  status?: Task['status']; // Legacy field
  task_status_id?: string; // New field for custom statuses
  priority?: Task['priority'];
  due_date?: string | null;
  estimated_hours?: number | null;
  actual_hours?: number;
  progress?: number;
  assignee_ids?: string[];
  label_ids?: string[];
  assigned_ai_teammate?: string; // AI teammate assignment
  assigned_user_agent_id?: string; // User agent ID assignment

  // Workflow assignment fields
  workflow_id?: string; // UUID of workflow to execute for this task
  workflow_execution_id?: string; // UUID of current workflow execution
}

export interface TaskFilters {
  status?: Task['status'] | Task['status'][];
  priority?: Task['priority'] | Task['priority'][];
  assignee?: string;
  project_id?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort_by?: 'created_at' | 'updated_at' | 'due_date' | 'priority' | 'title';
  sort_order?: 'asc' | 'desc';
}

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  status: 'active' | 'on_hold' | 'completed' | 'archived';
  owner_id: string;
  start_date?: string;
  end_date?: string;
  color?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  deleted_at?: string;

  // Computed fields
  task_count?: number;
  completed_task_count?: number;
  member_count?: number;
  progress?: number;
}

export interface Label {
  id: string;
  organization_id: string;
  name: string;
  color: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export type BulkAction = 'delete' | 'archive' | 'update_status' | 'update_priority' | 'assign' | 'add_labels' | 'remove_labels';

export interface BulkUpdatePayload {
  task_ids: string[];
  action: BulkAction;
  data?: {
    status?: Task['status'];
    priority?: Task['priority'];
    assignee_id?: string;
    label_ids?: string[];
  };
}

// Recurring Task Types
export interface RecurringTask {
  id: string;
  task_id: string;
  pattern: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  monthOfYear?: number;
  maxOccurrences?: number;
  endDate?: string;
  nextOccurrence?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRecurringTaskDTO {
  pattern: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  monthOfYear?: number;
  maxOccurrences?: number;
  endDate?: string;
}

export interface UpdateRecurringTaskDTO {
  pattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval?: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  monthOfYear?: number;
  maxOccurrences?: number;
  endDate?: string;
  isActive?: boolean;
}

// Task History Types
export interface TaskHistory {
  id: string;
  task_id: string;
  action: 'created' | 'updated' | 'completed' | 'deleted' | 'assigned' | 'status_changed';
  changes: Record<string, any>;
  changed_by: string;
  changed_by_name?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface TaskHistoryFilters {
  action?: string[];
  changed_by?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
}

// Reminder Types
export interface Reminder {
  id: string;
  user_id: string;
  conversation_id?: string;
  task_id?: string;
  title: string;
  description?: string;
  reminder_time: string;
  status: 'active' | 'completed' | 'cancelled' | 'expired';
  type: 'one_time' | 'recurring' | 'conditional';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  notification_channels: string[];
  notification_sent: boolean;
  notification_sent_at?: string;
  recurrence_pattern?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
  recurrence_interval?: number;
  recurrence_days_of_week?: number[];
  recurrence_end_date?: string;
  max_occurrences?: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreateReminderDTO {
  title: string;
  description?: string;
  reminder_time: string;
  conversation_id?: string;
  task_id?: string;
  type?: 'one_time' | 'recurring' | 'conditional';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  notification_channels?: string[];
  recurrence_pattern?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
  recurrence_interval?: number;
  recurrence_days_of_week?: number[];
  recurrence_end_date?: string;
  max_occurrences?: number;
  metadata?: Record<string, any>;
}

export interface UpdateReminderDTO {
  title?: string;
  description?: string;
  reminder_time?: string;
  status?: 'active' | 'completed' | 'cancelled';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  notification_channels?: string[];
  recurrence_pattern?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
  recurrence_interval?: number;
  recurrence_days_of_week?: number[];
  recurrence_end_date?: string;
  max_occurrences?: number;
  metadata?: Record<string, any>;
}

export interface ReminderFilters {
  status?: string[];
  type?: string[];
  priority?: string[];
  conversation_id?: string;
  task_id?: string;
  from_date?: string;
  to_date?: string;
  search_query?: string;
  limit?: number;
  offset?: number;
  sort_by?: 'reminder_time' | 'created_at' | 'updated_at' | 'priority';
  sort_order?: 'asc' | 'desc';
}

// Type aliases for better type safety
export type ReminderPriority = 'low' | 'medium' | 'high' | 'urgent';
export type ReminderType = 'one_time' | 'recurring' | 'conditional';
export type RecurrencePattern = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
export type NotificationChannel = 'chat' | 'email' | 'push';

// API Service class
class TodoService {
  private getAuthHeaders() {
    // Use same priority as auth store: authToken first, then token
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    };
  }

  // Task endpoints
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    console.log('=== getTasks API call ===', new Date().toISOString());
    console.trace('Call stack for getTasks');

    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks?${params}`, {
      headers: this.getAuthHeaders()
    });

    const tasks = response.data.tasks || response.data;
    console.log(`getTasks API response: ${tasks.length} tasks`);

    // Check for duplicates in API response
    const taskIds = tasks.map((t: Task) => t.id);
    const uniqueIds = new Set(taskIds);
    if (taskIds.length !== uniqueIds.size) {
      console.error('DUPLICATE TASKS IN API RESPONSE!');
      const duplicates = taskIds.filter((id: string, index: number) => taskIds.indexOf(id) !== index);
      console.error('Duplicate IDs from API:', duplicates);
    }

    return tasks;
  }

  async getTask(id: string): Promise<Task> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks/${id}/full`, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async createTask(task: CreateTaskDTO): Promise<Task> {
    const response = await axios.post(`${TODO_SERVICE_URL}/api/v1/tasks`, task, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async updateTask(id: string, updates: UpdateTaskDTO): Promise<Task> {
    const response = await axios.patch(`${TODO_SERVICE_URL}/api/v1/tasks/${id}`, updates, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async deleteTask(id: string): Promise<void> {
    await axios.delete(`${TODO_SERVICE_URL}/api/v1/tasks/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  async bulkUpdateTasks(payload: BulkUpdatePayload): Promise<Task[]> {
    const response = await axios.patch(`${TODO_SERVICE_URL}/api/v1/tasks/bulk`, payload, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async searchTasks(query: string): Promise<Task[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks/search?q=${encodeURIComponent(query)}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.tasks || response.data;
  }

  // Project endpoints
  async getProjects(): Promise<Project[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/projects`, {
      headers: this.getAuthHeaders()
    });
    return response.data.projects || response.data;
  }

  async getProject(id: string): Promise<Project> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/projects/${id}`, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async getProjectTasks(projectId: string, filters?: TaskFilters): Promise<Task[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/projects/${projectId}/tasks?${params}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.tasks || response.data;
  }

  async createProject(project: Partial<Project>): Promise<Project> {
    const response = await axios.post(`${TODO_SERVICE_URL}/api/v1/projects`, project, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async updateProject(id: string, updates: Partial<Project>): Promise<Project> {
    const response = await axios.patch(`${TODO_SERVICE_URL}/api/v1/projects/${id}`, updates, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await axios.delete(`${TODO_SERVICE_URL}/api/v1/projects/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  // Label endpoints
  async getLabels(): Promise<Label[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/labels`, {
      headers: this.getAuthHeaders()
    });
    return response.data.labels || response.data;
  }

  async createLabel(label: Partial<Label>): Promise<Label> {
    const response = await axios.post(`${TODO_SERVICE_URL}/api/v1/labels`, label, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  // Task comments
  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks/${taskId}/comments`, {
      headers: this.getAuthHeaders()
    });
    return response.data.comments || response.data;
  }

  async addTaskComment(taskId: string, text: string): Promise<TaskComment> {
    const response = await axios.post(`${TODO_SERVICE_URL}/api/v1/tasks/${taskId}/comments`,
      { text },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Subtasks
  async getSubtasks(taskId: string): Promise<Task[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks/${taskId}/subtasks`, {
      headers: this.getAuthHeaders()
    });
    return response.data.subtasks || response.data;
  }

  async createSubtask(parentTaskId: string, subtask: CreateTaskDTO): Promise<Task> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/tasks/${parentTaskId}/subtasks`,
      subtask,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Recurring Tasks
  async getRecurringTasks(): Promise<RecurringTask[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/recurring-tasks`, {
      headers: this.getAuthHeaders()
    });
    return response.data.recurringTasks || response.data;
  }

  async getRecurringTask(taskId: string): Promise<RecurringTask> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/recurring-tasks/task/${taskId}`, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async createRecurringTask(taskId: string, recurring: CreateRecurringTaskDTO): Promise<RecurringTask> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/recurring-tasks/${taskId}`,
      recurring,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateRecurringTask(taskId: string, updates: UpdateRecurringTaskDTO): Promise<RecurringTask> {
    const response = await axios.patch(
      `${TODO_SERVICE_URL}/api/v1/recurring-tasks/${taskId}`,
      updates,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteRecurringTask(taskId: string): Promise<void> {
    await axios.delete(`${TODO_SERVICE_URL}/api/v1/recurring-tasks/${taskId}`, {
      headers: this.getAuthHeaders()
    });
  }

  async generateRecurringInstances(taskId: string, months: number = 3): Promise<Task[]> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/recurring-tasks/${taskId}/generate`,
      { months },
      { headers: this.getAuthHeaders() }
    );
    return response.data.instances || response.data;
  }

  // Task History
  async getTaskHistory(taskId: string, filters?: TaskHistoryFilters): Promise<TaskHistory[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks/${taskId}/history?${params}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.history || response.data;
  }

  async getUserTaskHistory(filters?: TaskHistoryFilters): Promise<TaskHistory[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/tasks/history?${params}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.history || response.data;
  }

  // Reminders
  async getReminders(filters?: ReminderFilters): Promise<Reminder[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/reminders?${params}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.reminders || response.data;
  }

  async getReminder(id: string): Promise<Reminder> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/reminders/${id}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.reminder || response.data;
  }

  async createReminder(reminder: CreateReminderDTO): Promise<Reminder> {
    const response = await axios.post(`${TODO_SERVICE_URL}/api/v1/reminders`, reminder, {
      headers: this.getAuthHeaders()
    });
    return response.data.reminder || response.data;
  }

  async createReminderFromText(text: string, conversationId?: string): Promise<Reminder> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/reminders/from-text`,
      { text, conversation_id: conversationId },
      { headers: this.getAuthHeaders() }
    );
    return response.data.reminder || response.data;
  }

  async parseReminderText(text: string, conversationId?: string): Promise<any> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/reminders/parse`,
      { text, conversation_id: conversationId },
      { headers: this.getAuthHeaders() }
    );
    return response.data.parsed_reminder;
  }

  async updateReminder(id: string, updates: UpdateReminderDTO): Promise<Reminder> {
    const response = await axios.patch(`${TODO_SERVICE_URL}/api/v1/reminders/${id}`, updates, {
      headers: this.getAuthHeaders()
    });
    return response.data.reminder || response.data;
  }

  async deleteReminder(id: string): Promise<void> {
    await axios.delete(`${TODO_SERVICE_URL}/api/v1/reminders/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  async snoozeReminder(id: string, minutes: number): Promise<Reminder> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/reminders/${id}/snooze`,
      { minutes },
      { headers: this.getAuthHeaders() }
    );
    return response.data.reminder || response.data;
  }

  async completeReminder(id: string): Promise<Reminder> {
    const response = await axios.post(`${TODO_SERVICE_URL}/api/v1/reminders/${id}/complete`, {}, {
      headers: this.getAuthHeaders()
    });
    return response.data.reminder || response.data;
  }

  async getConversationReminders(conversationId: string): Promise<Reminder[]> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/reminders/conversation/${conversationId}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.reminders || response.data;
  }

  // Project Template Methods
  async getProjectTemplates(aiEnabled?: boolean): Promise<any[]> {
    const params = aiEnabled !== undefined ? `?ai_enabled=${aiEnabled}` : '';
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/project-templates${params}`, {
      headers: this.getAuthHeaders()
    });

    // Handle different response structures
    if (response.data && response.data.success) {
      // If response has success flag and data field
      return response.data.data || [];
    } else if (response.data && response.data.templates) {
      // If response has templates field
      return response.data.templates;
    } else if (Array.isArray(response.data)) {
      // If response is directly an array
      return response.data;
    } else {
      // Default to empty array if structure is unexpected
      console.warn('[TodoService] Unexpected project templates response:', response.data);
      return [];
    }
  }

  async getProjectTemplate(id: string): Promise<any> {
    const response = await axios.get(`${TODO_SERVICE_URL}/api/v1/project-templates/${id}`, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async suggestProjectTemplate(description: string): Promise<any> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/project-templates/suggest`,
      { description },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createProjectFromTemplate(projectData: any): Promise<any> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/projects`,
      projectData,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // AI Assignment Methods
  async createAIAssignment(data: any): Promise<any> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/ai-assignments`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getProjectAIAssignments(projectId: string): Promise<any[]> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/ai-assignments/project/${projectId}`,
      { headers: this.getAuthHeaders() }
    );
    const assignments = response.data.assignments || response.data;
    return Array.isArray(assignments) ? assignments : [];
  }

  async getAIAssignments(teammate?: string): Promise<any[]> {
    const params = teammate ? `?teammate=${teammate}` : '';
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/ai-assignments${params}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data.assignments || response.data;
  }

  async updateAIAssignment(id: string, updates: any): Promise<any> {
    const response = await axios.patch(
      `${TODO_SERVICE_URL}/api/v1/ai-assignments/${id}`,
      updates,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async checkAICapacity(teammate: string): Promise<any> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/ai-assignments/capacity/check?teammate=${teammate}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Task Status Management
  async getTaskStatuses(): Promise<TaskStatus[]> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/task-statuses`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createTaskStatus(status: {
    name?: string;
    label: string;
    color?: string;
    position?: number;
  }): Promise<TaskStatus> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/task-statuses`,
      status,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateTaskStatus(id: string, updates: {
    label?: string;
    color?: string;
    position?: number;
  }): Promise<TaskStatus> {
    const response = await axios.put(
      `${TODO_SERVICE_URL}/api/v1/task-statuses/${id}`,
      updates,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteTaskStatus(id: string): Promise<void> {
    await axios.delete(
      `${TODO_SERVICE_URL}/api/v1/task-statuses/${id}`,
      { headers: this.getAuthHeaders() }
    );
  }

  async getProjectStats(projectId: string): Promise<any> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/projects/${projectId}/tasks/stats`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getProjectTeamMembers(projectId: string): Promise<any[]> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/projects/${projectId}/members`,
      { headers: this.getAuthHeaders() }
    );
    return response.data.members || response.data || [];
  }

  async getProjectProgressHistory(projectId: string, days: number = 30): Promise<any[]> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/projects/${projectId}/progress-history?days=${days}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createTemplateFromProject(data: {
    projectId: string;
    name: string;
    description?: string;
    visibility?: 'private' | 'organization' | 'public';
  }): Promise<any> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/project-templates/from-project`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getOrganizationTemplates(): Promise<any[]> {
    const response = await axios.get(
      `${TODO_SERVICE_URL}/api/v1/project-templates/organization`,
      { headers: this.getAuthHeaders() }
    );
    return response.data.data || [];
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await axios.delete(
      `${TODO_SERVICE_URL}/api/v1/project-templates/${templateId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  async reorderTaskStatuses(statusOrder: string[]): Promise<TaskStatus[]> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/task-statuses/reorder`,
      { statusOrder },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async setDefaultTaskStatus(id: string): Promise<TaskStatus> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/task-statuses/${id}/set-default`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async processTaskAssignment(taskId: string): Promise<Task> {
    const response = await axios.post(
      `${TODO_SERVICE_URL}/api/v1/tasks/${taskId}/process-assignment`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export const todoService = new TodoService();